import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './RebateHistory.css';
import { buildApiUrl, API_ENDPOINTS } from '../config/api';

const RebateHistory = ({ user, token }) => {
  const [rebates, setRebates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);
  const loadMoreRef = useRef(null);
  
  // Memoized placeholder values to prevent recalculation during re-renders
  const placeholders = useMemo(() => ({
    agreementNumber: "Enter agreement number",
    customerName: "Enter customer name",
    fatherName: "Enter father's name",
    village: "Enter village name"
  }), []);

  // Filter state
  const [filters, setFilters] = useState({
    agreementNumber: '',
    customerName: '',
    fatherName: '',
    village: '',
    dateFrom: '',
    dateTo: ''
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    hasMore: false
  });
  
  // Summary state
  const [summary, setSummary] = useState({
    totalRebatesIssued: 0,
    totalRebateAmount: 0
  });

  const loadRebates = useCallback(async (customFilters = null, requestedPage = pagination.page) => {
    const requestId = ++requestIdRef.current;
    loadingRef.current = true;
    setLoading(true);
    setError('');

    try {
      // Use custom filters if provided, otherwise use state filters
      const filtersToUse = customFilters !== null ? customFilters : filters;
      
      const queryParams = new URLSearchParams({
        page: requestedPage,
        limit: pagination.limit,
        ...filtersToUse
      });

      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.REBATES)}?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to load rebates');
      }

      if (requestId !== requestIdRef.current) return;
      setRebates(prev => requestedPage === 1
        ? result.data
        : [...prev, ...result.data]);
      setPagination(prev => ({
        ...prev,
        page: requestedPage,
        total: result.pagination.total,
        hasMore: result.pagination.hasMore
      }));
      setSummary(result.summary);

    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Load rebates error:', err);
      setError(err.message);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        loadingRef.current = false;
      }
    }
  }, [filters, pagination.limit, pagination.page, token]);

  // Load rebates on component mount and when filters change
  useEffect(() => {
    loadRebates();
  }, [loadRebates]);

  useEffect(() => {
    if (!pagination.hasMore || loading || error || rebates.length === 0) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loadingRef.current) {
        loadingRef.current = true;
        setPagination(prev => ({ ...prev, page: prev.page + 1 }));
      }
    });

    const sentinel = loadMoreRef.current;
    if (sentinel) observer.observe(sentinel);
    return () => observer.disconnect();
  }, [pagination.hasMore, loading, error, rebates.length]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      page: 1,
      total: 0,
      hasMore: false
    }));
  };

  const handleSearch = () => {
    if (loading) return; // Prevent multiple clicks
    if (pagination.page === 1) {
      setPagination(prev => ({ ...prev, total: 0, hasMore: false }));
      loadRebates(filters, 1);
    } else {
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: 0,
        hasMore: false
      }));
    }
  };

  const handleClearFilters = () => {
    if (loading) return; // Prevent multiple clicks
    
    setFilters({
      agreementNumber: '',
      customerName: '',
      fatherName: '',
      village: '',
      dateFrom: '',
      dateTo: ''
    });
    setPagination(prev => ({
      ...prev,
      page: 1,
      total: 0,
      hasMore: false
    }));
    // Don't call loadRebates() - useEffect will handle it automatically
  };

  const handleExport = async () => {
    if (exporting || loading || pagination.total === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Please allow pop-ups to export the rebate history.');
      return;
    }

    setExporting(true);
    setError('');
    try {
      const exportRows = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const queryParams = new URLSearchParams({
          page,
          limit: 100,
          ...filters
        });
        const response = await fetch(`${buildApiUrl(API_ENDPOINTS.REBATES)}?${queryParams}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to fetch rebate history for export');
        }

        exportRows.push(...result.data);
        hasMore = result.pagination.hasMore;
        page += 1;
      }

      if (exportRows.length === 0) {
        printWindow.close();
        setError('No rebate records to export.');
        return;
      }

      const escapeHtml = (value) => String(value ?? '--').replace(/[&<>"']/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[character]);
      const formatDate = value => {
        if (!value) return '--';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '--' : date.toLocaleString('en-IN');
      };
      const rows = exportRows.map(rebate => {
        const customer = rebate.agreementId?.reservationId?.customerId;
        return `
          <tr>
            <td>${escapeHtml(rebate.agreementNumber)}</td>
            <td>${escapeHtml(rebate.agreementId?.lotNumber)}</td>
            <td>${escapeHtml(customer?.customerName)}</td>
            <td>${escapeHtml(customer?.fatherName)}</td>
            <td>${escapeHtml(customer?.addressLine1)}</td>
            <td>${escapeHtml(rebate.agreementId?.weightInQuintal)}</td>
            <td>${escapeHtml(rebate.agreementId?.numberOfBags)}</td>
            <td>₹${escapeHtml(Number(rebate.originalPayableAmount || 0).toLocaleString('en-IN'))}</td>
            <td>₹${escapeHtml(Number(rebate.rebateAmount || 0).toLocaleString('en-IN'))}</td>
            <td>₹${escapeHtml(Number(rebate.netPayableAfterRebate || 0).toLocaleString('en-IN'))}</td>
            <td>${escapeHtml(rebate.rebateType === 'amount' ? 'Fixed Amount' : 'Per Quintal')}</td>
            <td>${escapeHtml(rebate.issuedByName || rebate.issuedBy)}</td>
            <td>${escapeHtml(formatDate(rebate.issuedOn))}</td>
            <td>${escapeHtml(rebate.status)}</td>
          </tr>`;
      }).join('');
      const filterLabels = {
        agreementNumber: 'Agreement Number',
        customerName: 'Customer Name',
        fatherName: "Father's Name",
        village: 'Village Name',
        dateFrom: 'Date From',
        dateTo: 'Date To'
      };
      const activeFilters = Object.entries(filters)
        .filter(([, value]) => value?.trim())
        .map(([key, value]) => `<li>${escapeHtml(filterLabels[key])}: ${escapeHtml(value.trim())}</li>`)
        .join('');

      printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Rebate History Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #222; }
            h1, .report-meta { text-align: center; }
            .filters { margin: 20px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
            th { background: #f1f1f1; }
            tr { page-break-inside: avoid; }
            .print-action { text-align: center; margin: 20px; }
            .print-action button { padding: 10px 20px; cursor: pointer; }
            @media print {
              body { padding: 0; }
              .print-action { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Rebate History Report</h1>
          <p class="report-meta">Generated on: ${escapeHtml(new Date().toLocaleString('en-IN'))}</p>
          <p class="report-meta">Total records: ${exportRows.length}</p>
          ${activeFilters
            ? `<section class="filters"><strong>Filters applied:</strong><ul>${activeFilters}</ul></section>`
            : '<p>Filters applied: None</p>'}
          <table>
            <thead>
              <tr>
                <th>Agreement No.</th><th>Lot No.</th><th>Customer Name</th><th>Father's Name</th>
                <th>Village</th><th>Weight (Quintals)</th><th>Bags</th><th>Original Amount</th>
                <th>Rebate Amount</th><th>Net Payable</th><th>Rebate Type</th><th>Issued By</th>
                <th>Issued On</th><th>Status</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="print-action"><button onclick="window.print()">Print / Save as PDF</button></div>
        </body>
      </html>
      `);
      printWindow.document.close();
    } catch (err) {
      printWindow.close();
      console.error('Export rebates error:', err);
      setError(err.message || 'Failed to export rebate history.');
    } finally {
      setExporting(false);
    }
  };

  const handleCancelRebate = async (rebateId) => {
    const reason = prompt('Please enter the reason for cancelling this rebate:');
    if (!reason) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.REBATES)}/${rebateId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cancellationReason: reason
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to cancel rebate');
      }

      setSuccess('Rebate cancelled successfully');
      if (pagination.page === 1) {
        loadRebates(filters, 1);
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Cancel rebate error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rebate-history">
      <div className="page-header">
        <h2>📊 Rebate History</h2>
        <p>View and manage all issued rebates</p>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <h3>Search & Filter</h3>
        <div className="filters-form">
          <div className="filter-row">
            <div className="form-group">
              <label>Agreement Number</label>
              <input
                type="text"
                name="agreementNumber"
                value={filters.agreementNumber}
                onChange={handleFilterChange}
                placeholder={placeholders.agreementNumber}
              />
            </div>
            <div className="form-group">
              <label>Customer Name</label>
              <input
                type="text"
                name="customerName"
                value={filters.customerName}
                onChange={handleFilterChange}
                placeholder={placeholders.customerName}
              />
            </div>
            <div className="form-group">
              <label>Father&apos;s Name</label>
              <input
                type="text"
                name="fatherName"
                value={filters.fatherName}
                onChange={handleFilterChange}
                placeholder={placeholders.fatherName}
              />
            </div>
            <div className="form-group">
              <label>Village Name</label>
              <input
                type="text"
                name="village"
                value={filters.village}
                onChange={handleFilterChange}
                placeholder={placeholders.village}
              />
            </div>
            <div className="form-group">
              <label>Date From</label>
              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group">
              <label>Date To</label>
              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button onClick={handleSearch} className="btn-search" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
            <button onClick={handleClearFilters} className="btn-clear">
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Summary Section */}
      <div className="summary-section">
        <div className="summary-card">
          <div className="summary-item">
            <label>Total Rebates Issued:</label>
            <span className="summary-value">{summary.totalRebatesIssued}</span>
          </div>
          <div className="summary-item">
            <label>Total Rebate Amount:</label>
            <span className="summary-value">₹{summary.totalRebateAmount.toLocaleString('en-IN')}</span>
          </div>
        </div>
      </div>

      {/* Rebates Table */}
      <div className="rebates-section">
        <div className="rebates-header">
          <h3>Rebate Records</h3>
          <button
            onClick={handleExport}
            disabled={exporting || loading || pagination.total === 0}
            className="btn-export-pdf"
            title="Export all matching rebate records to PDF"
          >
            {exporting ? '⏳ Fetching all data...' : '📄 Export PDF'}
          </button>
        </div>
        
        {rebates.length > 0 ? (
          <>
            <div className="table-container">
              <table className="rebates-table">
                <thead>
                  <tr>
                    <th>Agreement Number</th>
                    <th>Lot Number</th>
                    <th>Vendor Name</th>
                    <th>Weight (Quintals)</th>
                    <th>Bags</th>
                    <th>Original Amount</th>
                    <th>Rebate Amount</th>
                    <th>Net Payable</th>
                    <th>Rebate Type</th>
                    <th>Issued By</th>
                    <th>Issued On</th>
                    {user?.role === 'ADMIN' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {rebates.map((rebate) => (
                    <tr key={rebate._id}>
                      <td>{rebate.agreementNumber}</td>
                      <td>{rebate.agreementId?.lotNumber || '-'}</td>
                      <td>{rebate.agreementId?.reservationId?.customerId?.customerName || '-'}</td>
                      <td>{rebate.agreementId?.weightInQuintal || '-'}</td>
                      <td>{rebate.agreementId?.numberOfBags || '-'}</td>
                      <td>₹{rebate.originalPayableAmount.toLocaleString('en-IN')}</td>
                      <td className="rebate-amount">-₹{rebate.rebateAmount.toLocaleString('en-IN')}</td>
                      <td className="net-payable">₹{rebate.netPayableAfterRebate.toLocaleString('en-IN')}</td>
                      <td>
                        <span className={`rebate-type ${rebate.rebateType}`}>
                          {rebate.rebateType === 'amount' ? 'Fixed Amount' : 'Per Quintal'}
                        </span>
                      </td>
                      <td>{rebate.issuedByName || rebate.issuedBy || '-'}</td>
                      <td>
                        {new Date(rebate.issuedOn).toLocaleDateString()} {new Date(rebate.issuedOn).toLocaleTimeString()}
                      </td>
                      {user?.role === 'ADMIN' && (
                        <td>
                          {rebate.status === 'active' && (
                            <button
                              onClick={() => handleCancelRebate(rebate._id)}
                              className="btn-cancel"
                              disabled={loading}
                            >
                              Cancel
                            </button>
                          )}
                          {rebate.status === 'cancelled' && (
                            <span className="cancelled-info">
                              Cancelled by {rebate.cancelledByName || rebate.cancelledBy}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination-info" aria-live="polite">
              Showing {rebates.length} of {pagination.total} rebates
            </div>
            {loading && <div className="loading-more">Loading rebates...</div>}
            {pagination.hasMore && !error && (
              <div ref={loadMoreRef} className="load-more-sentinel" aria-hidden="true" />
            )}
          </>
        ) : (
          <div className="no-data-message">
            {loading ? (
              <p>Loading rebates...</p>
            ) : (
              <p>No rebates found. {filters.agreementNumber || filters.customerName || filters.fatherName || filters.village || filters.dateFrom || filters.dateTo ? 'Try adjusting your search criteria.' : 'No rebates have been issued yet.'}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RebateHistory;